<template>
  <Teleport to="body">
    <div
      v-if="showTour"
      :style="{ position: 'fixed', top, left, 'z-index': 2007 }"
    >
      <v-card
        id="tour-guide"
        elevation="8"
        role="tooltip"
        width="300"
        :dark="!theme.current.value.dark"
      >
        <v-card-title class="text-center">
          {{ tourTitle }}
        </v-card-title>
        <v-card-text class="text-center">
          {{ tourText }}
        </v-card-text>
        <v-card-actions>
          <app-button
            class="text-none"
            variant="text"
            size="small"
            data-testid="tour-previous"
            :disabled="currentStep === 0 || loading"
            @click="previous"
          >
            Previous
          </app-button>
          <app-button
            class="text-none"
            small
            variant="text"
            data-testid="tour-skip"
            @click="tourSkipped"
          >
            Skip
          </app-button>
          <v-spacer />
          <app-button
            v-if="currentStep === steps.length - 1"
            color="primary"
            class="text-none"
            size="small"
            data-testid="tour-complete"
            @click="tourFinished"
          >
            Complete
          </app-button>
          <app-button
            v-else
            color="primary"
            class="text-none"
            size="small"
            data-testid="tour-next"
            :loading="loading"
            @click="next"
          >
            Next
          </app-button>
        </v-card-actions>
      </v-card>
      <div id="arrow" data-popper-arrow></div>
    </div>
  </Teleport>
</template>
<script setup>
import { useUserStore } from "@/stores/user";
import { useDialogStore } from "@/stores/dialog";
import { useTheme } from "vuetify";
import { computed, markRaw, nextTick, onMounted, ref } from "vue";
import WelcomeTourDialog from "@/dialogs/WelcomeTourDialog.vue";
import ConfirmationDialog from "@/dialogs/ConfirmationDialog.vue";
const theme = useTheme();
const showTour = ref(false);
const currentStep = ref(0);
const tourTitle = ref(null);
const tourText = ref(null);

const user = useUserStore();
const dialog = useDialogStore();

const top = ref(0);
const left = ref(0);
const steps = ref([]);
const loading = ref(false);

const currentStepObj = computed(() => steps.value[currentStep.value]);

function setSteps() {
  const s = [];

  if (document.getElementById("launch-quote-and-apply")) {
    s.push({
      target: "#launch-quote-and-apply",
      title: "Launch Quote & Apply",
      text: "You can launch Quote & Apply from any page with this button.",
      placement: "bottom"
    });
  }
  s.push({
    target: "#user-menu",
    title: "User Menu",
    text: "View helpful options like account settings, viewing your profile, changing light/dark mode, and logging out.",
    placement: "bottom",
    before: () => hideUserMenu()
  });

  s.push({
    target: "#v-step-2",
    title: "User Settings",
    text: "Settings allows you to edit your account and contracting information.",
    placement: "left",
    before: () => {
      showUserMenu();
    }
  });

  s.push({
    target: "#v-step-3",
    title: "Home Page",
    text: "Check out your top cases and recent activity from the home tab",
    placement: "right",
    before: () => hideUserMenu()
  });

  s.push({
    target: "#quote-and-apply-nav",
    title: "Quote & Apply Dashboard",
    text: "View Quote & Apply Sites and eApps",
    placement: "right"
  });

  s.push({
    target: "#cases-nav",
    title: "Case Dashboard",
    text: "View your cases",
    placement: "right"
  });

  if (document.getElementById("commissions-nav")) {
    s.push({
      target: "#commissions-nav",
      title: "Commission Dashboard",
      text: "Track your commissions and view product commissions",
      placement: "right"
    });
  }

  if (document.getElementById("schedule-a-meeting")) {
    s.push({
      target: "#schedule-a-meeting",
      title: "Schedule a Meeting",
      text: "If you have any more questions, click here to schedule a meeting!",
      placement: "bottom"
    });
  }

  return s;
}

function showUserMenu() {
  const userMenu = document.getElementById("user-menu");
  if (!userMenu) return;
  if (userMenu.getAttribute("aria-expanded") === "true") return;
  userMenu.click();
}
function hideUserMenu() {
  const userMenu = document.getElementById("user-menu");
  if (!userMenu) return;
  if (userMenu.getAttribute("aria-expanded") === "false") return;
  userMenu.click();
}
async function startTour() {
  const res = await dialog.showDialog({
    component: markRaw(WelcomeTourDialog),
    persistent: true
  });
  if (res?.stop) {
    tourSkipped();
    return;
  }

  showTour.value = true;
  nextTick(() => {
    renderPopover();
  });
}
function tourSkipped() {
  user.completeTutorial("home");
  user.saveTutorials();
  hideUserMenu();
}
function tourFinished() {
  tourSkipped();
  dialog.showDialog({
    component: markRaw(ConfirmationDialog),
    title: "Welcome Tour Completed",
    subtitle: "You've completed the introductory tour of BOSS.",
    hideCancel: true,
    confirmText: "Continue to BOSS"
  });
}
function previous() {
  currentStep.value--;
  nextTick(() => {
    renderPopover();
  });
}
function next() {
  currentStep.value++;
  nextTick(() => {
    renderPopover();
  });
}
async function renderPopover() {
  loading.value = true;
  if (currentStepObj.value.before) {
    currentStepObj.value.before();
    await nextTick();
    await new Promise(res => setTimeout(res, 250));
  }

  tourTitle.value = currentStepObj.value.title;
  tourText.value = currentStepObj.value.text;
  const target = document.querySelector(currentStepObj.value.target);
  target.setAttribute("aria-describedby", "tooltip");

  const targetRect = target.getBoundingClientRect();
  const offset = 8; // 8px offset
  const tourGuide = document.getElementById("tour-guide");
  const tourGuideRect = tourGuide.getBoundingClientRect();

  let topPosition = targetRect.top + offset;
  let leftPosition = targetRect.left + offset;

  // Adjust positioning based on placement direction
  switch (currentStepObj.value.placement) {
    case "top":
      topPosition = targetRect.top - tourGuideRect.height - offset;
      break;
    case "bottom":
      topPosition = targetRect.top + targetRect.height + offset;
      break;
    case "left":
      leftPosition = targetRect.left - tourGuideRect.width - offset * 2;
      break;
    case "right":
      leftPosition = targetRect.left + targetRect.width + offset * 2;
      break;
  }

  // Prevent going off screen
  const windowWidth = window.innerWidth;
  const windowHeight = window.innerHeight;

  // Adjust horizontal position
  if (leftPosition + tourGuideRect.width > windowWidth) {
    leftPosition = windowWidth - tourGuideRect.width - offset;
  }
  if (leftPosition < 0) {
    leftPosition = offset;
  }

  // Adjust vertical position
  if (topPosition + tourGuideRect.height > windowHeight) {
    topPosition = windowHeight - tourGuideRect.height - offset;
  }
  if (topPosition < 0) {
    topPosition = offset;
  }

  top.value = `${topPosition}px`;
  left.value = `${leftPosition}px`;

  // Scroll target into view
  target.scrollIntoView({ behavior: "smooth", block: "center" });

  // Position arrow based on placement
  const arrow = document.getElementById("arrow");
  arrow.style.position = "absolute";
  arrow.style.top = null;
  arrow.style.bottom = null;
  arrow.style.left = null;
  arrow.style.right = null;

  switch (currentStepObj.value.placement) {
    case "top":
      arrow.style.bottom = "-8px";
      arrow.style.left = "50%";
      arrow.style.transform = "translateX(-50%) rotate(45deg)";
      break;
    case "bottom":
      arrow.style.top = "-8px";
      arrow.style.left = "50%";
      arrow.style.transform = "translateX(-50%) rotate(45deg)";
      break;
    case "left":
      arrow.style.right = "-8px";
      arrow.style.top = "15%";
      arrow.style.transform = "translateY(-50%) rotate(45deg)";
      break;
    case "right":
      arrow.style.left = "-8px";
      arrow.style.top = "15%";
      arrow.style.transform = "translateY(-50%) rotate(45deg)";
      break;
  }

  loading.value = false;
}
onMounted(() => {
  steps.value = setSteps();
  startTour();
});
</script>

<style scoped lang="scss">
#arrow {
  width: 16px;
  height: 16px;
  background: rgb(var(--v-theme-surface));
  visibility: visible;
  border: inherit;
}
</style>
